// #ux-redesign-start: ProfileInitial — Datencheck profesional (Patients)
// Pantalla post-login en /admin/initial. El paciente confirma que sus datos
// personales + anschrift están al día antes de continuar al app.
//
// Diseño:
//   - PageContainer con icon + title + subtitle (todo i18n).
//   - Banner intro explicando POR QUÉ se pide este check.
//   - Dos SectionCards lado a lado en md+, apiladas en mobile.
//     Cada card usa dl/dt/dd para jerarquía label/value clara y tematizada.
//   - Botones sticky al pie del card en mobile (full-width), inline en desktop.
//
// Lógica preservada 1:1:
//   - actionGetProfile al mount.
//   - Redirect si NO hay pending_initial_choice.
//   - "Korrigieren" → /admin/profile + limpia flag.
//   - "Ja, weiter" → respeta iframe redirect / on_delete / is_ready / tokenInfo.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslate } from "react-translate";
import moment from "moment";

import { actionGetProfile } from "store/actions/profilesActions";
import storageDispatcher from "helpers/storageDispatcher";
import Dates from "../../helpers/Dates";

import { Button, PageContainer, SectionCard } from "components/ds";

const iframeURLGET = require("configs/app.json").iframeURLGET;

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const InfoIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const ProfileInitial = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const isMount = useRef(true);

  const { profile } = useSelector((state) => state.profilesReducer);
  const user = useSelector((state) => state.userReducer);
  const user_id = user._id ? user._id : null;

  const t = useTranslate("profile-initial");
  const tProfile = useTranslate("profile");
  const tAddress = useTranslate("profile-address");

  const companyType =
    profile && profile.company && profile.company.type
      ? profile.company.type.toLowerCase().replace(" ", "-")
      : "";

  useEffect(() => {
    if (isMount.current && user_id) {
      dispatch(actionGetProfile());
    }
    return () => {
      isMount.current = false;
    };
  }, [dispatch, user_id]);

  useEffect(() => {
    if (!localStorage.getItem("pending_initial_choice")) {
      history.replace(
        user.on_delete
          ? "/admin/delete-confirmation"
          : !user.is_ready
          ? "/admin/profile"
          : user.tokenInfo
          ? "/admin/laboratories"
          : "/admin/dashboard"
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCorrect = () => {
    storageDispatcher.removeLocalStorageEvent("pending_initial_choice");
    history.replace("/admin/profile");
  };

  const handleContinue = () => {
    storageDispatcher.removeLocalStorageEvent("pending_initial_choice");
    const isIframeRedirect =
      user.is_ready && localStorage.getItem("redirect") === iframeURLGET;
    history.replace(
      isIframeRedirect
        ? "/admin/frametest"
        : user.on_delete
        ? "/admin/delete-confirmation"
        : !user.is_ready
        ? "/admin/profile"
        : user.tokenInfo
        ? "/admin/laboratories"
        : "/admin/dashboard"
    );
    if (isIframeRedirect) {
      localStorage.removeItem("redirect");
    }
  };

  const address = profile && profile.address && profile.address[0];
  const fullName = `${user.pronoun || ""} ${user.title || ""} ${user.firstName || ""} ${user.lastName || ""}`.trim();
  const birthdayFormatted = user.birthday
    ? moment(Dates.parseDate(user.birthday)).format("DD.MM.YYYY")
    : null;

  // Field row (label/value) — solo render si hay valor (clean empty state).
  const Field = ({ label, value }) =>
    value ? (
      <div className="ds-initial__field">
        <dt>{label}</dt>
        <dd>{value}</dd>
      </div>
    ) : null;

  const countryLabel =
    address && address.country
      ? typeof address.country.label !== "undefined"
        ? address.country.label
        : isNaN(address.country.value)
        ? address.country.value
        : ""
      : "";

  return (
    <PageContainer
      title={t("title")}
      subtitle={t("subtitle")}
      icon="user-active"
    >
      <div className="ds-initial">
        <div className="ds-initial__intro">
          <span className="ds-initial__intro-icon" aria-hidden="true">
            <InfoIcon />
          </span>
          <p>{t("intro")}</p>
        </div>

        <div className="ds-initial__grid">
          <SectionCard title={t("section-personal")}>
            <dl className="ds-initial__list">
              <Field label={t("label-name")} value={fullName} />
              <Field
                label={t("label-gender")}
                value={
                  user.gender
                    ? tProfile(`gender-${user.gender.toLowerCase()}`)
                    : null
                }
              />
              <Field label={t("label-birthday")} value={birthdayFormatted} />
              <Field label={t("label-phone")} value={user.phone} />

              {profile.company &&
              (profile.company.name ||
                companyType ||
                profile.company.number) ? (
                <>
                  <Field
                    label={t("label-insurance")}
                    value={profile.company.name}
                  />
                  <Field
                    label={t("label-insurance-type")}
                    value={companyType ? tAddress(companyType) : null}
                  />
                  <Field
                    label={t("label-insurance-number")}
                    value={profile.company.number}
                  />
                </>
              ) : (
                <div className="ds-initial__empty">{t("empty-insurance")}</div>
              )}
            </dl>
          </SectionCard>

          <SectionCard title={t("section-address")}>
            {address ? (
              <dl className="ds-initial__list">
                <Field
                  label={t("label-name")}
                  value={`${address.firstName || ""} ${address.lastName || ""}`.trim()}
                />
                <Field
                  label={t("label-street")}
                  value={
                    address.scope === "packstation"
                      ? `${address.packstation || ""} ${address.packstationNumber || ""}`.trim()
                      : `${address.street || ""} ${address.streetNumber || ""}`.trim()
                  }
                />
                <Field
                  label={t("label-city")}
                  value={
                    address.postalCode && address.city
                      ? `${address.postalCode} · ${address.city}`
                      : address.postalCode || address.city
                  }
                />
                <Field label={t("label-country")} value={countryLabel} />
                <Field label={t("label-phone")} value={address.phone} />
              </dl>
            ) : (
              <div className="ds-initial__empty">{t("empty-address")}</div>
            )}
          </SectionCard>
        </div>

        <div className="ds-initial__actions">
          <Button
            variant="secondary"
            size="lg"
            onClick={handleCorrect}
            iconLeft={<EditIcon />}
            name="btn-initial-correct"
          >
            {t("btn-correct")}
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={handleContinue}
            iconLeft={<CheckIcon />}
            name="btn-initial-continue"
          >
            {t("btn-continue")}
          </Button>
        </div>
      </div>
    </PageContainer>
  );
};

export default ProfileInitial;
// #ux-redesign-end
